import React, {useCallback, useEffect, useState} from 'react';
import {Box, FormControlLabel, Radio, RadioGroup} from '@mui/material';
import PropTypes from 'prop-types';
import EditMultiFieldsBuilder from './Shared/EditMultiFieldsBuilder';
import RichEditorResultText from './Shared/RichEditorResultText';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

const SingleChoice = ({
                          index,
                          componentId,
                          fieldKey,
                          value,
                          onChange,
                          descriptionHtml,
                          onDescriptionHtmlChange,
                          commitDescriptionHtml,
                          isEditMode,
                          enableValidation,
                          options,
                          isFormArchived,
                          relatedComponent,
                          readOnly,
                          placeholderValues,
                          accessLevel,
                          error,
                          audience,
                          recipientType,
                          linkedToStudents,
                          linkedToEmployees,
                          submitMode
                      }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <EditMultiFieldsBuilder
                        fieldKey={fieldKey}
                        onChange={onChange}
                        enableValidation={enableValidation}
                        description={draftDescriptionHtml}
                        index={index}
                        options={options}
                        isFormArchived={isFormArchived}
                        relatedComponent={relatedComponent}
                        accessLevel={accessLevel}
                        currentDescription={draftDescriptionHtml}
                        setCurrentDescription={handleDraftChange}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        linkedToEmployees={linkedToEmployees}
                        placeholderValues={placeholderValues}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                )
                : (
                    <Box key={`${descriptionHtml}-${value}`}>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            error={!!error}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            submitMode={submitMode}
                        />
                    <FormControl error={!!error} variant="standard">
                        <RadioGroup
                            value={value}
                            onClick={(e) =>
                                onChange({
                                    id: componentId,
                                    radioValue: e.target.value === value ? null : e.target.value
                                })}
                        >
                            {options.map((option, idx) => (
                                <FormControlLabel
                                    key={`${descriptionHtml}-option-${idx}`}
                                    sx={{
                                        '& .MuiTypography-root': {
                                            wordBreak: 'break-word',
                                            maxWidth: '800px',
                                        }
                                    }}
                                    value={option.id}
                                    control={<Radio size="small"/>}
                                    label={option.description}
                                />
                            ))}
                        </RadioGroup>
                        <FormHelperText>{error}</FormHelperText>
                    </FormControl>
                </Box>
                )}
        </Box>
    );
};

SingleChoice.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fieldKey: PropTypes.string,
    value: PropTypes.string,
    index: PropTypes.number,
    options: PropTypes.array,
    onChange: PropTypes.func,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func,
    commitDescriptionHtml: PropTypes.func,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    relatedComponent: PropTypes.object,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default SingleChoice;
